import { useCallback, useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { getEndorsementSend, sendEndorsement } from '@/services/documentSends.service.js';
import { DOCUMENTS_CHANGED_EVENT, announceDocumentsChanged } from '@/services/deals.service.js';
import { CcField, CcResult, DocumentLine, MissingItem, ccList, earlierSends, sendWord } from './sendSheetParts.jsx';

// "Send endorsement to <client>" (paperwork-1 D11, D12: every live endorsement row, whatever its money, gets this click).
// L1a's stub, filled by L3-2: the button opens a dialog that names the carrier's endorsement (derived name, original filename)
// and the client's own address, lists what is missing with its button (attach the carrier's endorsement, confirm it, the
// client's record), sends it attached (claimed in document_sends before the mail) and shows what was sent, read back. L2
// places it on each live endorsement row of the Transaction History; its label and props are L1a's, unchanged.
//
// @param {object}  props
// @param {string}  props.dealId         the deal (CRM deal id)
// @param {string}  props.transactionId  the endorsement's policy_transactions row
// @param {string}  props.clientName     the client as a person reads it (the named insured), never an id
// @param {string}  [props.className]
// @param {Function} [props.onSent]      called with the send's answer (read back)
function EndorsementDialog({ dealId, transactionId, clientName, open, onOpenChange, onSent }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // "Add others (CC)" (round 2, Q21): copied on the email to the client; never on the automatic send.
  const [cc, setCc] = useState('');
  const load = useCallback(async () => {
    setError(null);
    try { setPreview(await getEndorsementSend(transactionId)); }
    catch (e) { setPreview(null); setError(e instanceof ApiError ? e.body?.error || e.code : e.message); }
  }, [transactionId]);
  useEffect(() => { if (open) { setResult(null); setCc(''); load(); } }, [open, load]);
  useEffect(() => {
    const on = (ev) => { if (open && String(ev?.detail?.dealId) === String(dealId)) load(); };
    window.addEventListener(DOCUMENTS_CHANGED_EVENT, on);
    return () => window.removeEventListener(DOCUMENTS_CHANGED_EVENT, on);
  }, [open, dealId, load]);

  async function handleSend() {
    setBusy(true);
    try {
      const out = await sendEndorsement(transactionId, preview.document.id, ccList(cc));
      setResult(out);
      const s = out.sends[0];
      if (s && s.delivered) toast.success(`${preview.document.name} sent to ${out.sent_to}`);
      else toast.info(`${preview.document.name}: ${sendWord(s)}`);
      announceDocumentsChanged(dealId);
      if (typeof onSent === 'function') onSent(out);
    } catch (e) {
      const body = e instanceof ApiError ? e.body : null;
      if (body?.preview) { setPreview(body.preview); toast.info(`Not sent: ${body.error}`); }
      else if (body?.sends) { setResult(body); toast.error(`Not sent: ${body.error}`); }
      else toast.error(`Not sent: ${body?.error || e.message}`);
    } finally { setBusy(false); }
  }

  const client = preview?.client;
  const who = client?.label || clientName;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-endorsement-send>
        <DialogHeader>
          <DialogTitle>Send endorsement to {clientName || who}</DialogTitle>
          <DialogDescription>The carrier's endorsement, attached, to {who && who !== clientName ? `${who}, ${clientName}'s contact,` : who} at their own email address. Nothing goes until you press Send.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {!preview && !error ? <p className="text-sm text-text-muted">Reading the endorsement...</p> : null}
          {error ? <p className="text-sm text-warning">{error}</p> : null}
          {preview ? (
            <>
              <p className="text-[13px] text-text">{preview.transaction.description || 'Endorsement'}{preview.transaction.effective_date ? `, effective ${preview.transaction.effective_date}` : ''}</p>
              {preview.document ? <ul><DocumentLine doc={{ ...preview.document, earlier_sends: earlierSends(preview.sends, result) }} /></ul> : null}
              <p className="text-[12px] text-text-muted">To: {client?.address || 'no email address'}</p>
              {preview.missing.length ? <ul className="space-y-1.5">{preview.missing.map((m) => <MissingItem key={m.action} item={m} ctx={{ dealId, client }} onDone={load} />)}</ul> : null}
              {!result && client?.address ? <CcField value={cc} onChange={setCc} to={who || client.address} disabled={busy} /> : null}
            </>
          ) : null}
          {result ? <div className="rounded-card border border-border p-2 text-[13px]" data-send-result><p>{(result.sends || []).map((s) => sendWord(s)).join('; ') || 'Nothing was recorded'}</p><CcResult out={result} to={who} /></div> : null}
        </div>
        <DialogFooter className="sm:flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{result ? 'Close' : 'Cancel'}</Button>
          {!result ? (
            <Button variant="accent" className="h-auto min-h-9 whitespace-normal" disabled={busy || !preview?.sendable} onClick={handleSend} data-send-endorsement>
              <Send size={14} strokeWidth={2} />{busy ? 'Sending...' : `Send the endorsement to ${who}`}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SendEndorsementButton({ dealId, transactionId, clientName, className, onSent }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" size="sm" className={className} data-deal-id={dealId} data-transaction-id={transactionId} disabled={!transactionId} onClick={() => setOpen(true)}>
        {`Send endorsement to ${clientName}`}
      </Button>
      {open ? <EndorsementDialog dealId={dealId} transactionId={transactionId} clientName={clientName} open={open} onOpenChange={setOpen} onSent={onSent} /> : null}
    </>
  );
}
